<template>
  <div data-view="google-manager">
    <Can
      action="view"
      subject="ad-manager.order"
    >
      <AddNewOrderDialog
        v-if="isAddNewOrderModalOn"
        :is-on="isAddNewOrderModalOn"
        :is-loading="isAddingOrder"
        :on-cancel="toggleNewOrderModal"
        :on-confirm="createOrder"
        :open-on-create="selectOrder"
      />
    </Can>
    <Can
      action="view-business-contact"
      subject="ad-manager.order"
    >
      <dialog-empty :is-on="isAddNewOrderStepperOn" :on-close="toggleNewOrderStepper">
        <template v-slot:content>
          <AddNewOrderStepper
            v-if="isAddNewOrderStepperOn"
            ref="promostepper"
            :promoted-deal="{}"
            :flow="promotionFlow"
            :on-cancel="toggleNewOrderStepper"
            :on-confirm="createOrder"
            :on-update="updateOrder"
            :on-create-li="createLineItem"
            :select-on-create="selectOrder"
          />
        </template>
      </dialog-empty>
    </Can>

    <EditOrderDialog
      v-if="isEditOrderModalOn"
      :is-on="isEditOrderModalOn"
      :is-business="isBusiness"
      :order="selectedOrder"
      :is-editing-order="isEditingOrder"
      :can-edit-details="canEditDetails"
      :on-cancel="toggleEditOrderModal"
      :on-confirm="updateOrder"
      :get-orders="getFilteredOrders"
      :is-loading-order="isLoading"
      :on-toggle="toggleEditDetails"
      :on-change-status="updateStatus"
    />

    <layout-logged-frame title="Promotions" has-before>
      <template v-slot:header-actions>
        <Can
          action="view"
          subject="ad-manager.order"
        >
          <v-btn
            color="primary"
            @click="toggleNewOrderModal"
          >
            <v-icon>add</v-icon>
            {{ $t('ad_manager_add_new_title') }}
          </v-btn>
        </Can>

        <Can
          action="view-business-contact"
          subject="ad-manager.order"
        >
          <v-btn
            id="new-promotion"
            color="primary"
            :disabled="!hasEnabledForPromotionCountries"
            @click="openNewOrderStepper"
          >
            <v-icon>add</v-icon>
            {{ $t('new_promotion') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:before>
        <NavigationTabs :tabs="navigationTabs"/>
      </template>

      <template v-slot:content>
        <PromotionsAlerts
          v-if="isBusiness"
          :incomplete="incompleteBilling"
          :pending="pendingBilling"
          :denied="deniedBilling"
        />

        <!-- if enabledPromoCountries is empty then the platform select will use user/countries-->
        <PromotionsFilters
          ref="ordersfilter"
          :is-skeleton-loading="isSkeletonLoading"
          :countries="enabledPromoCountries"
          @filter="onFilter"
          @clearFilters="onClearFilters"
        />

        <table-base
          :columns="isBusiness ? columnsBC : columns"
          :items="orders"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          :can-edit-columns="false"
          :on-select-item="selectOrder"
          :on-change-page="onChangePage"
          :on-update-pagination="onUpdatePagination"
          has-custom-items-template
        >
          <template
            v-slot:item-cell="scope"
            
          >
            <template v-if="scope.cell.column.value === 'isArchived'">
              {{ displayArchivedColumn(scope.cell.item) }}
            </template>

            <template v-else-if="scope.cell.column.value === 'lines'">
              <LineItemStatus
                :items="scope.cell.item"
                :item-name="scope.cell.item.name"
                :expanded="expandedRows"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'impressions'">
              <LineItemImpressions
                :items="scope.cell.item"
                :item-name="scope.cell.item.name"
                :expanded="expandedRows"
                :on-toggle="onToggleRow"
              />
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
import PromotionsFilters from '../../components/forms/filters/Promotions';
import AddNewOrderDialog from '../../components/dialog/order/AddNew';
import AddNewOrderStepper from '../../components/steppers/Promotion';
import EditOrderDialog from '../../components/dialog/order/Edit';
import LineItemStatus from '../../components/panels/lineItems/Status';
import LineItemImpressions from '../../components/panels/lineItems/Impressions';
import PromotionsAlerts from '../../components/alerts/Promotions';
import columns from '../../config/promotions/orderColumns';
import columnsBC from '../../config/promotions/orderColumnsBC';
import navigationTabs from '../../config/promotions/navigationTabs';
import NavigationTabs from '../../components/NavigationTabs'
import {extractNestedProp, isNum, isObj, isStr} from '@/utils';

export default {
    components: {
      PromotionsFilters,
      AddNewOrderDialog,
      EditOrderDialog,
      AddNewOrderStepper,
      LineItemStatus,
      LineItemImpressions,
      PromotionsAlerts,
      NavigationTabs,
    },
    data() {
      return {
        navigationTabs,
        columns,
        columnsBC,
        totalPages: 0,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: '',
          totalItems: 0,
          search: ''
        },
        query: {},
        selectedOrder: {},
        createdOrder: {},
        expandedRows: [],
        urlOrderId: null,
        promotionFlow: '',
        isSkeletonLoading: true,
        isLoading: false,
        isAddNewOrderModalOn: false,
        isEditOrderModalOn: false,
        isAddNewOrderStepperOn: false,
        isAddingOrder: false,
        isEditingOrder: false,
        canEditDetails: false
      };
    },
    computed: {
      disabledForPromotionCountries() {
        const { disabledForPromotionCountries } = this.$store.getters['businesses/activeItem'] || {};
        return disabledForPromotionCountries || [];
      },
      hasEnabledForPromotionCountries() {
        return this.disabledForPromotionCountries.length !== this.enabledPromoCountries.length;
      },
      activeUser() {
        return this.$store.getters['auth/user'];
      },
      enabledPromoCountries() {
        return this.$store.getters['businessUsers/enabledPromoCountries'];
      },
      orders() {
        return this.$store.getters['promotions/items'];
      },
      billingCountriesByStatus() {
        const { billingCountriesByStatus } = this.$store.getters['businesses/activeItem'];
        return billingCountriesByStatus || [];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      canUpdateOrder() {
        return this.$ability.can('update', 'ad-manager.order') || this.activeUser.business_id;
      },
      isBusiness() {
        return this.$ability.can('view-business-contact', 'ad-manager.order');
      },
      incompleteBilling() {
        return this.alertMessageBaseCondition('no_billing');
      },
      pendingBilling() {
        return this.alertMessageBaseCondition('networks_in_review');
      },
      deniedBilling() {
        return this.alertMessageBaseCondition('networks_denied');
      },
    },
    created() {
      const { $route, getOrders } = this;

      if ($route.params.id) {
        this.urlOrderId = Number($route.params.id);
      }

      getOrders()
        .then(res => {
          this.preSelectOrder(res);
          return res;
        })
        .catch(error => error);
    },
    methods: {
      getOrders({
        page = 1,
        order = '',
        sort = '',
        search = '',
        status = -1,
        countries = [],
        businessId = -1,
        advertiserId = -1,
        date_filter = '',
        created_by = '',
        contract_name = -1,
        order_type = -1
      } = {}) {
        const query = {
          page,
          order,
          sort,
          search,
          status,
          countries,
          date_filter,
          businessId,
          advertiserId,
          archived: 1,
          created_by,
          contract_name,
          order_type
        };

        this.isLoading = true;

        return this.$store.dispatch('promotions/getOrders', query)
          .then(res => {
            const { itemsMeta } = this.$store.getters['promotions/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isSkeletonLoading = false;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isSkeletonLoading = false;
            this.isLoading = false;

            return err;
          });
      },
      getFilteredOrders() {
        if (this.$refs.ordersfilter) {
          this.$refs.ordersfilter.onFilter();
        }
      },
      onFilter(filters) {
        const name = filters.name || '';
        const status = filters.status?.id || -1;
        const businessId = filters.business_id || -1;
        const advertiserId = filters.advertiser_id || -1;
        const created_by = filters.created_by || '';
        const date_filter = filters.dates || '';
        const contract_name = filters.contract_name || -1;
        const order_type = filters.order_type || -1;
        const countries = filters.countries;

        this.query = {
          ...this.query,
          ...this.getSearchQuery(filters),
          name,
          status,
          businessId,
          advertiserId,
          date_filter,
          created_by,
          page: 1,
          contract_name,
          order_type,
          countries
        };

        this.getOrders(this.query);
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        this.query = {sort: sortBy};

        this.getOrders(this.query);
      },
      getSearchQuery({ search, status, dates, businessId, advertiserId, created_by }) {
        const query = {};

        query.search = search;
        query.status = status;
        query.businessId = businessId;
        query.advertiserId = advertiserId;
        query.date_filter = dates;
        query.created_by = created_by;

        return query;
      },
      toggleNewOrderModal() {
        this.isAddNewOrderModalOn = !this.isAddNewOrderModalOn;
      },
      toggleNewOrderStepper() {
        if (this.isAddNewOrderStepperOn && this.$refs.promostepper && this.$refs.promostepper.$refs.addnewlikb) {
          this.$refs.promostepper.$refs.addnewlikb.clear();
        }

        this.isAddNewOrderStepperOn = !this.isAddNewOrderStepperOn;
      },
      openNewOrderStepper(event) {
        if (isObj(event) && isStr(event.currentTarget.id)) {
          this.promotionFlow = event.currentTarget.id;
        }

        this.toggleNewOrderStepper();
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
      displayArchivedColumn(item) {
        return item.isArchived ? 'Yes' : 'No';
      },
      createLineItem(lineItem) {
        return this.$store.dispatch('promotions/createLineItem', lineItem)
          .then(res => {
            this.getOrders();
            return res;
          });
      },
      createOrder(order) {
        this.isAddingOrder = true;

        return this.$store.dispatch('promotions/createOrder', order)
          .then(res => {
            this.isAddingOrder = false;

            if (res.success) {
              const { order, sortBy } = this.pagination;
              const nextQuery = {
                ...this.query,
                order,
                sort: sortBy
              };

              this.getOrders(nextQuery);

              this.isAddNewOrderModalOn = false;
              this.createdOrder = res.data.data.data;
            }

            return res;
          });
      },
      async updateOrder(order) {
        if (!this.canUpdateOrder) {
          return undefined;
        }

        const nextOrder = {
          ...order,
          id: this.selectedOrder.id || this.createdOrder.id,
          isBC: this.isBusiness
        };

        this.isEditingOrder = true;

        return this.$store.dispatch('promotions/updateOrder', nextOrder)
          .then(res => {
            this.isEditOrderModalOn = false;

            this.getFilteredOrders();

            return res;
          })
          .catch((err) => err)
          .finally(() => {
            this.isEditingOrder = false;
          });
      },
      updateStatus(status) {
        if (!this.canUpdateOrder) {
          return undefined;
        }

        const nextOrder = {
          ...status,
          id: this.selectedOrder.id,
          statusId: status.id,
          isBC: this.isBusiness
        };

        this.isEditingOrder = true;

        return this.$store.dispatch('promotions/updateStatus', nextOrder)
          .then(() => {
            const { sortBy } = this.pagination;
            const nextQuery = {
              ...this.query,
              sort: sortBy
            };

            this.isEditingOrder = false;
            this.isEditOrderModalOn = false;

            return this.getOrders(nextQuery);
          })
          .catch((err) => {
            this.isEditingOrder = false;
            return err;
          });
      },
      selectOrder(order) {
        this.selectedOrder = order;
        this.isEditOrderModalOn = true;

        if (this.isBusiness && isObj(order) && isObj(order.business) && isObj(order.advertiser)) {
          this.getBusinessPlacements(order.business.id, order.advertiser.id, order.country?.code);
        }

        return this.$store.dispatch('promotions/setActiveOrder', order);
      },
      preSelectOrder(res) {
        if (this.urlOrderId && res.items) {
          const activeOrder = res.items.filter(e => e.id === this.urlOrderId).shift();

          if (isObj(activeOrder) && isNum(activeOrder.id)) {
            this.selectOrder(activeOrder);
          }
        }
      },
      getBusinessPlacements(business_id, advertiser_id, country) {
        const query = {
          business_id,
          advertiser_id,
          country
        };

        return this.$store.dispatch('businesses/getBusinessPlacements', query)
          .then(res => res.placements)
          .catch(err => err);
      },
      toggleEditOrderModal() {
        if (!this.isAddNewOrderModalOn) {
          this.canEditDetails = false;
        }
        this.isEditOrderModalOn = !this.isEditOrderModalOn;
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy
        };
        this.query = nextQuery;

        this.getOrders(nextQuery);
      },
      onUpdatePagination({ page, order, sortBy }) {
        if (!sortBy) return null;

        const { query, pagination } = this;
        this.pagination.order = order;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: this.pagination.sortBy,
        };
        this.query = nextQuery;

        this.getOrders(nextQuery);
      },
      onToggleRow(name) {
        const itemIndex = this.expandedRows.findIndex(i => i === name);

        if (itemIndex > -1) {
          this.expandedRows.splice(itemIndex, 1);
        } else {
          this.expandedRows.push(name);
        }
      },
      toggleEditDetails() {
        this.canEditDetails = !this.canEditDetails;
      },
      alertMessageBaseCondition(arrayName) {
        const { billingCountriesByStatus } = this;

        // eslint-disable-next-line max-len
        if ( billingCountriesByStatus && billingCountriesByStatus[arrayName] && billingCountriesByStatus[arrayName].length) {
          return billingCountriesByStatus[arrayName];
        }

        return [];
      },
    }
  };
</script>

<style lang="scss">

</style>
